import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl } from '@angular/forms';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BoardConfig } from 'src/app/shared/services/tutorial.service';
import { BoardPermissionsService } from 'src/app/shared/services/board-permissions.service';
import { NotificationService } from 'src/app/shared/services/notification.service';

@Component({
  selector: 'app-up-board-admin-config',
  templateUrl: './up-board-admin-config.component.html',
  styleUrls: ['./up-board-admin-config.component.css']
})
export class UpBoardAdminConfigComponent implements OnInit, OnDestroy {

  icons = icons;
  public boardId: string | undefined;
  public showDelivery: boolean = true;
  public highlightDelivery: boolean = false;
  public saving: boolean = false;

  // Purchased flags from super-admin (controls which channels are available to configure)
  public instoreChannelPurchased: boolean = true;  // Default true for backward compatibility
  public phoneChannelPurchased: boolean = false;
  public internetChannelPurchased: boolean = false;

  // Multi-channel configuration form
  configForm = new FormGroup({
    showDelivery: new FormControl(true),
    enableInstoreChannel: new FormControl(true),    // Walk-ins channel - default true for backward compatibility
    enablePhoneChannel: new FormControl(false),
    enableOnlineChannel: new FormControl(false),
    allowUpToTakePhone: new FormControl(false),
    allowUpToTakeInternet: new FormControl(false),
    allowOnDeckToTakePhone: new FormControl(true),
    allowOnDeckToTakeInternet: new FormControl(true),
    useModernLayout: new FormControl(false)
  });
  
  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private db: AngularFirestore,
    private route: ActivatedRoute,
    private auth: AngularFireAuth,
    private boardPermissionsService: BoardPermissionsService,
    private notificationService: NotificationService
  ) { }

  /**
   * Toggles the showDelivery flag and persists it to Firestore
   */
  toggleDeliveryFeature() {
    const current = this.configForm.get('showDelivery')?.value ?? true;
    this.configForm.patchValue({ showDelivery: !current });
    this.saveConfig();
  }

  /**
   * Saves the current config to Firestore after admin authorization check
   */
  async saveConfig() {
    if (!this.boardId) return;

    try {
      this.saving = true;

      // Check admin authorization
      const isAdmin = this.boardPermissionsService.userHasAdminPermissions(this.boardPermissionsService.boardUser);
      if (!isAdmin) {
        this.notificationService.showError('Admin authorization required');
        this.saving = false;
        return;
      }

      // Get form values as BoardConfig, filtering out null values
      const formValue = this.configForm.value as any;
      const config: any = {};
      for (const key in formValue) {
        if (formValue[key] !== null && formValue[key] !== undefined) {
          config[key] = formValue[key];
        }
      }

      // DEBUG: Log config being saved for Story 3.9 debugging
      console.log('[DEBUG Story 3.9] Raw form value:', JSON.stringify(formValue));
      console.log('[DEBUG Story 3.9] useModernLayout in formValue:', formValue.useModernLayout, 'type:', typeof formValue.useModernLayout);
      console.log('[DEBUG Story 3.9] Saving config to Firestore:', JSON.stringify(config));
      console.log('[DEBUG Story 3.9] useModernLayout value being saved:', config.useModernLayout, 'type:', typeof config.useModernLayout);

      // Write to Firestore with merge pattern
      await this.db.collection('boards')
        .doc(this.boardId)
        .set({ config }, { merge: true });

      this.notificationService.showSuccess('Board configuration saved successfully');
      this.saving = false;
    } catch (error) {
      console.error('Error saving config:', error);
      this.notificationService.showError('Failed to save configuration. Please try again.');
      this.saving = false;
    }
  }

  ngOnInit(): void {
    // Check for highlight query parameter from tutorial navigation
    const queryParamSub = this.route.queryParams.subscribe(params => {
      this.highlightDelivery = params['highlight'] === 'showDelivery';
      
      // Auto-remove highlight after 5 seconds
      if (this.highlightDelivery) {
        setTimeout(() => {
          this.highlightDelivery = false;
        }, 5000);
      }
    });
    this.subscriptions.push(queryParamSub);
    
    const authSub = this.auth.user.subscribe(u => {
      if (!u || !u.email) return;
      if (!this.route.parent) return;

      let boardId = this.route.parent.snapshot.paramMap.get('id');

      if (!boardId) return;
      this.boardId = boardId;

      // Initialize board permissions service
      this.boardPermissionsService.init(boardId, u.email);

      // Subscribe to board document for current config value
      const boardSub = this.db.collection('boards')
        .doc<any>(this.boardId)
        .valueChanges()
        .pipe(takeUntil(this.destroy$))
        .subscribe(b => {
          if (b?.config) {
            // Update purchased flags from super-admin settings
            // Default instore to true if undefined (backward compatibility)
            this.instoreChannelPurchased = b.config.instoreChannelPurchased !== false;
            this.phoneChannelPurchased = b.config.phoneChannelPurchased === true;
            this.internetChannelPurchased = b.config.internetChannelPurchased === true;

            // Populate form with existing config
            this.configForm.patchValue(b.config);
            
            // IMPORTANT: Ensure disabled channels are not enabled if the channel is not purchased
            // This prevents stale enablePhoneChannel/enableOnlineChannel values from forcing modern UI
            if (!this.phoneChannelPurchased) {
              this.configForm.patchValue({ enablePhoneChannel: false });
            }
            if (!this.internetChannelPurchased) {
              this.configForm.patchValue({ enableOnlineChannel: false });
            }
          } else {
            // Initialize with defaults if no config yet
            this.instoreChannelPurchased = true;
            this.phoneChannelPurchased = false;
            this.internetChannelPurchased = false;

            this.configForm.reset({
              showDelivery: true,
              enableInstoreChannel: true,    // Walk-ins channel - default true for backward compatibility
              enablePhoneChannel: false,
              enableOnlineChannel: false,
              allowUpToTakePhone: false,
              allowUpToTakeInternet: false,
              allowOnDeckToTakePhone: true,
              allowOnDeckToTakeInternet: true,
              useModernLayout: false
            });
          }

          // Update legacy showDelivery property for template compatibility
          this.showDelivery = b?.config?.showDelivery ?? true;
        });
      this.subscriptions.push(boardSub);
    });
    this.subscriptions.push(authSub);
  }
  
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }
}
